import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BaseAiService } from '../admin-core/utils/base-ai.service';
import { normalizePebblComponents } from './product-cards.util';
import { ServiceTokenService } from '../admin-core/services/auth/service-token.service';
import { AUTH_CLIENT, AUTH_CLIENT_HEADER } from '../admin-core/utils/auth-client';
import {
  AttachmentUploadTicket,
  ChatMessage,
  ConfirmedAttachment,
  Conversation,
  DocumentPreview,
  FeedbackCategory,
  FeedbackItem,
  FeedbackPage,
  FeedbackRating,
  FeedbackStatus,
  KnowledgeDoc,
  MessageAttachment,
  ModelOption,
  ModelsInfo,
  PebblStreamEvent,
  SheetSelectionInput,
  UsageInfo,
  WebsiteChatCustomer,
  WebsiteChatDetail,
  WebsiteChatMessage,
  WebsiteChatPage,
  WebsiteChatQuery,
  WebsiteChatRow,
  WebsiteChatSummary,
  WebsiteCustomerGroup,
  WebsiteCustomerPage,
  WebsiteCustomerSort,
  WebsiteCustomerThread,
} from './ai.models';

/** Raw token-usage/cost record from the backend (snake_case `TokenUsageRecord`). */
interface ApiTokenUsage {
  model: string;
  input_tokens?: number;
  cached_input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  input_cost?: number;
  output_cost?: number;
  total_cost?: number;
  currency?: string;
  elapsed_seconds?: number | null;
}

/** Normalise a backend snake_case usage record to the camelCase view-model. */
export function mapUsage(u: ApiTokenUsage): UsageInfo {
  return {
    model: u.model,
    inputTokens: u.input_tokens ?? 0,
    cachedInputTokens: u.cached_input_tokens ?? 0,
    outputTokens: u.output_tokens ?? 0,
    totalTokens: u.total_tokens ?? 0,
    inputCost: u.input_cost ?? 0,
    outputCost: u.output_cost ?? 0,
    totalCost: u.total_cost ?? 0,
    currency: u.currency ?? 'USD',
    elapsedSeconds: u.elapsed_seconds ?? undefined,
  };
}

/* ---- Raw API payload shapes (backend envelope `.payload`) ---- */

interface ApiConversation {
  uuid: string;
  title: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

interface ApiConversationList {
  items: ApiConversation[];
  next_cursor: string | null;
}

interface ApiConversationInit {
  uuid: string;
  created_at: string;
}

interface ApiFeedback {
  rating: FeedbackRating;
  comment?: string | null;
}

/** An image bound to a stored message, with a live (presigned) view URL. */
interface ApiMessageAttachment {
  attachment_uuid: string;
  url: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  width?: number | null;
  height?: number | null;
}

interface ApiMessage {
  uuid: string;
  role: 'user' | 'assistant';
  content: string;
  components?: unknown[];
  /** Absent on messages stored before attachment support. */
  attachments?: ApiMessageAttachment[];
  feedback?: ApiFeedback | null;
  usage?: ApiTokenUsage | null;
  /** When the message was stored, as a tz-aware ISO string. */
  created_at?: string;
}

interface ApiMessageList {
  items: ApiMessage[];
  next_cursor: number | null;
}

/* ---- Domain page wrappers ---- */

export interface ConversationPage {
  items: Conversation[];
  nextCursor: string | null;
}

export interface MessagePage {
  items: ChatMessage[];
  nextCursor: number | null;
}

/** A parsed upload response: the stored (pending) document plus its parse preview. */
export interface UploadedDoc {
  documentUuid: string;
  fileName: string;
  needsColumnSelection: boolean;
  preview?: DocumentPreview;
  /** Markdown body preview (product-info docs); absent for tabular files. */
  text?: string;
}

/** One image the client wants to upload, described before its bytes exist. */
export interface AttachmentUploadIntent {
  fileName: string;
  contentType: string;
  sizeBytes: number;
  width?: number;
  height?: number;
}

/** A document's full detail — the list fields plus whether it needs column selection. */
export interface KnowledgeDocDetail extends KnowledgeDoc {
  needsColumnSelection: boolean;
}

/**
 * Typed client for the Pebbl backend (`/pebbl/*` on `environment.aiApiUrl`), mirroring
 * gatherco-ai-v2/frontend/src/lib/api.ts. REST calls reuse {@link BaseAiService} so they
 * carry the logged-in GatherCo bearer token; the chat stream is a fetch-based SSE reader
 * (Angular's HttpClient cannot stream SSE cleanly). Trace/usage data is still received and
 * persisted server-side but is not surfaced by this UI.
 */
@Injectable({ providedIn: 'root' })
export class PebblApiService {
  private readonly base = 'pebbl';

  /** Cached model list — the roster is process-wide static, so fetch it once and share. */
  private models$?: Observable<ModelsInfo>;

  /* ################### Constructor Declaration Start ################### */
  constructor(
    private baseAiService: BaseAiService,
    private serviceTokenService: ServiceTokenService,
  ) {}
  /* ################### Constructor Declaration End ################### */

  /************************************
   * Models start
   ************************************/
  /** The available chat models plus the backend's default, cached for the session. */
  listModels(): Observable<ModelsInfo> {
    if (!this.models$) {
      this.models$ = this.baseAiService.get(`${this.base}/models`).pipe(
        map((res: any) => {
          const payload = res.body?.payload ?? {};
          const models: ModelOption[] = (payload.models ?? []).map((m: any) => ({
            value: m.id,
            label: m.label,
            // The blurb has gone by a few names across backend revisions; take
            // whichever one this payload carries.
            description: m.description ?? m.hint ?? m.blurb ?? m.subtitle ?? m.tagline ?? undefined,
            thinkingLevels: (m.thinking_levels ?? []).map((l: any) => ({
              value: l.id,
              label: l.label,
              description: l.description ?? undefined,
            })),
            defaultThinkingLevel: m.default_thinking_level ?? null,
          }));
          return { models, defaultModel: payload.default_model ?? null };
        }),
        // shareReplay caches a FAILURE just as happily as a result: without this, one bad response
        // (an aged-out service token 401ing on the first open) would be replayed to every later
        // subscriber for the life of the tab, so the model picker never came back even once auth was
        // healthy again. Drop the cache on error so the next opener re-requests.
        catchError((err) => {
          this.models$ = undefined;
          return throwError(() => err);
        }),
        shareReplay(1),
      );
    }
    return this.models$;
  }
  /************************************
   * Models end
   ************************************/

  /************************************
   * Conversations start
   ************************************/
  listConversations(
    opts: { limit?: number; cursor?: string | null; search?: string } = {},
  ): Observable<ConversationPage> {
    const params = new URLSearchParams();
    if (opts.limit != null) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    if (opts.search) params.set('search', opts.search);
    return this.baseAiService.get(`${this.base}/conversations?${params}`).pipe(
      map((res: any) => {
        const payload = res.body?.payload as ApiConversationList;
        return {
          items: (payload?.items ?? []).map((c) => this.mapConversation(c)),
          nextCursor: payload?.next_cursor ?? null,
        };
      }),
    );
  }

  /** Mint a bare conversation up front, returning its generated UUID (no messages). */
  initConversation(): Observable<{ id: string; createdAt: string }> {
    return this.baseAiService.post(`${this.base}/conversations/init`, {}).pipe(
      map((res: any) => {
        const payload = res?.payload as ApiConversationInit;
        return { id: payload.uuid, createdAt: payload.created_at };
      }),
    );
  }

  getConversation(id: string): Observable<Conversation> {
    return this.baseAiService.get(`${this.base}/conversations/${encodeURIComponent(id)}`).pipe(
      map((res: any) => this.mapConversation(res.body?.payload as ApiConversation)),
    );
  }

  renameConversation(id: string, title: string): Observable<Conversation> {
    return this.baseAiService.patch(`${this.base}/conversations/${encodeURIComponent(id)}`, { title }).pipe(
      map((res: any) => this.mapConversation(res?.payload as ApiConversation)),
    );
  }

  deleteConversation(id: string): Observable<void> {
    return this.baseAiService.delete(`${this.base}/conversations/${encodeURIComponent(id)}`).pipe(
      map(() => undefined),
    );
  }

  listMessages(id: string, opts: { limit?: number; cursor?: number | null } = {}): Observable<MessagePage> {
    const params = new URLSearchParams();
    if (opts.limit != null) params.set('limit', String(opts.limit));
    if (opts.cursor != null) params.set('cursor', String(opts.cursor));
    return this.baseAiService
      .get(`${this.base}/conversations/${encodeURIComponent(id)}/messages?${params}`)
      .pipe(
        map((res: any) => {
          const payload = res.body?.payload as ApiMessageList;
          return {
            items: (payload?.items ?? []).map((m) => this.mapMessage(m)),
            nextCursor: payload?.next_cursor ?? null,
          };
        }),
      );
  }
  /************************************
   * Conversations end
   ************************************/

  /************************************
   * Feedback start
   ************************************/
  /** Set (or replace) the thumbs rating on an assistant message. */
  setFeedback(conversationId: string, messageId: string, rating: FeedbackRating): Observable<void> {
    return this.baseAiService
      .put(`${this.base}/feedback/rating`, {
        conversation_uuid: conversationId,
        message_uuid: messageId,
        rating,
      })
      .pipe(map(() => undefined));
  }

  /**
   * Submit a standalone feedback item (the thumbs-down popup): a category bucket and
   * a free-text message, both required. `POST /pebbl/feedback` starts it at 'pending'.
   */
  createFeedback(category: FeedbackCategory, message: string): Observable<void> {
    return this.baseAiService
      .post(`${this.base}/feedback`, { category, message })
      .pipe(map(() => undefined));
  }

  /** Clear any thumbs rating from an assistant message (un-vote). */
  clearFeedback(conversationId: string, messageId: string): Observable<void> {
    const params = new URLSearchParams({
      conversation_uuid: conversationId,
      message_uuid: messageId,
    });
    return this.baseAiService
      .delete(`${this.base}/feedback/rating?${params}`)
      .pipe(map(() => undefined));
  }

  /** A page of submitted feedback items (newest first), for the review section. */
  listFeedback(
    opts: { limit?: number; cursor?: string | null; status?: FeedbackStatus | null } = {},
  ): Observable<FeedbackPage> {
    const params = new URLSearchParams();
    if (opts.limit != null) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    if (opts.status) params.set('status', opts.status);
    return this.baseAiService.get(`${this.base}/feedback?${params}`).pipe(
      map((res: any) => {
        const payload = res.body?.payload as { items?: any[]; next_cursor?: string | null };
        return {
          items: (payload?.items ?? []).map((f) => this.mapFeedback(f)),
          nextCursor: payload?.next_cursor ?? null,
        };
      }),
    );
  }

  /** Patch a feedback item's status (e.g. mark as completed → 'resolved'). */
  updateFeedbackStatus(uuid: string, status: FeedbackStatus): Observable<FeedbackItem> {
    return this.baseAiService
      .patch(`${this.base}/feedback/${encodeURIComponent(uuid)}`, { status })
      .pipe(map((res: any) => this.mapFeedback(res?.payload)));
  }

  /** Remove a feedback item. */
  deleteFeedback(uuid: string): Observable<void> {
    return this.baseAiService
      .delete(`${this.base}/feedback/${encodeURIComponent(uuid)}`)
      .pipe(map(() => undefined));
  }

  /* ---- Website Insights (staff only, `/pebbl/admin/website-chats`) ---- */

  /** KPI totals for website chats started in the period. */
  getWebsiteInsightsSummary(from: Date, to: Date): Observable<WebsiteChatSummary> {
    const params = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
    return this.baseAiService
      .get(`${this.base}/admin/website-chats/summary?${params}`)
      .pipe(map((res: any) => this.mapWebsiteSummary(res.body?.payload)));
  }

  /** One page of website conversations for the period, filtered and sorted server-side. */
  listWebsiteChats(query: WebsiteChatQuery): Observable<WebsiteChatPage> {
    const params = this.websiteListParams(query);
    return this.baseAiService.get(`${this.base}/admin/website-chats?${params}`).pipe(
      map((res: any) => {
        const p = res.body?.payload ?? {};
        return {
          total: p.total ?? 0,
          page: p.page ?? 1,
          size: p.size ?? query.size ?? 25,
          items: (p.items ?? []).map((r: any) => this.mapWebsiteRow(r)),
        };
      }),
    );
  }

  /**
   * One page of website customers, each with their matching conversations. Same filters
   * as {@link listWebsiteChats}; the server groups, sorts and pages by customer.
   */
  listWebsiteCustomers(query: WebsiteChatQuery<WebsiteCustomerSort>): Observable<WebsiteCustomerPage> {
    const params = this.websiteListParams(query);
    return this.baseAiService.get(`${this.base}/admin/website-chats/customers?${params}`).pipe(
      map((res: any) => {
        const p = res.body?.payload ?? {};
        return {
          total: p.total ?? 0,
          conversations: p.conversations ?? 0,
          page: p.page ?? 1,
          size: p.size ?? query.size ?? 25,
          items: (p.items ?? []).map((g: any) => this.mapWebsiteGroup(g)),
        };
      }),
    );
  }

  /**
   * One customer's matching chats merged into a single conversation, in one request.
   * Same period, rating filter and search as the lists, so it holds the chats their row counts.
   */
  getWebsiteCustomerThread(
    userId: number,
    query: Pick<WebsiteChatQuery, 'from' | 'to' | 'rating' | 'q'>,
  ): Observable<WebsiteCustomerThread> {
    const params = new URLSearchParams({
      from: query.from.toISOString(),
      to: query.to.toISOString(),
      rating: query.rating ?? 'all',
    });
    const q = query.q?.trim();
    if (q) params.set('q', q);
    return this.baseAiService
      .get(`${this.base}/admin/website-chats/customers/${encodeURIComponent(userId)}?${params}`)
      .pipe(
        map((res: any) => {
          const t = res.body?.payload ?? {};
          return {
            truncated: !!t.truncated,
            segments: (t.segments ?? []).map((seg: any) => ({
              conversation: this.mapWebsiteRow(seg.conversation),
              messages: (seg.messages ?? []).map((m: any) => this.mapWebsiteMessage(m)),
            })),
          };
        }),
      );
  }

  private websiteListParams(query: WebsiteChatQuery<string>): URLSearchParams {
    const params = new URLSearchParams({
      from: query.from.toISOString(),
      to: query.to.toISOString(),
      rating: query.rating ?? 'all',
      sort: query.sort ?? 'started',
      dir: query.dir ?? 'desc',
      page: String(query.page ?? 1),
      size: String(query.size ?? 25),
    });
    const q = query.q?.trim();
    if (q) params.set('q', q);
    return params;
  }

  /** Full transcript of one website conversation (deleted-by-customer chats included). */
  getWebsiteChat(conversationUuid: string): Observable<WebsiteChatDetail> {
    return this.baseAiService
      .get(`${this.base}/admin/website-chats/${encodeURIComponent(conversationUuid)}`)
      .pipe(map((res: any) => this.mapWebsiteDetail(res.body?.payload)));
  }

  private mapWebsiteSummary(p: any): WebsiteChatSummary {
    return {
      customers: p?.customers ?? 0,
      accounts: p?.accounts ?? 0,
      returningCustomers: p?.returning_customers ?? 0,
      conversations: p?.conversations ?? 0,
      questions: p?.questions ?? 0,
      answers: p?.answers ?? 0,
      answersIncompleteUsage: p?.answers_incomplete_usage ?? 0,
      images: p?.images ?? 0,
      inputTokens: p?.input_tokens ?? 0,
      cachedInputTokens: p?.cached_input_tokens ?? 0,
      outputTokens: p?.output_tokens ?? 0,
      totalTokens: p?.total_tokens ?? 0,
      costUsd: p?.cost_usd ?? 0,
      thumbsUp: p?.thumbs_up ?? 0,
      thumbsDown: p?.thumbs_down ?? 0,
      replySecondsSum: p?.reply_seconds_sum ?? 0,
      replyCount: p?.reply_count ?? 0,
      currency: p?.currency ?? 'USD',
    };
  }

  private mapWebsiteCustomer(c: any): WebsiteChatCustomer {
    return {
      userId: c?.user_id ?? null,
      name: c?.name ?? '',
      email: c?.email ?? '',
      account: c?.account ?? '',
      accountId: c?.account_id ?? null,
    };
  }

  private mapWebsiteRow(r: any): WebsiteChatRow {
    return {
      conversationUuid: r.conversation_uuid,
      title: r.title ?? '',
      lastQuestion: r.last_question ?? '',
      startedAt: r.started_at,
      deletedByCustomer: !!r.deleted_by_customer,
      customer: this.mapWebsiteCustomer(r.customer),
      questions: r.questions ?? 0,
      imageCount: r.image_count ?? 0,
      thumbsUp: r.thumbs_up ?? 0,
      thumbsDown: r.thumbs_down ?? 0,
      totalTokens: r.total_tokens ?? 0,
      costUsd: r.cost_usd ?? 0,
    };
  }

  private mapWebsiteGroup(g: any): WebsiteCustomerGroup {
    return {
      customer: this.mapWebsiteCustomer(g.customer),
      conversations: g.conversations ?? 0,
      firstStartedAt: g.first_started_at,
      lastStartedAt: g.last_started_at,
      questions: g.questions ?? 0,
      imageCount: g.image_count ?? 0,
      thumbsUp: g.thumbs_up ?? 0,
      thumbsDown: g.thumbs_down ?? 0,
      totalTokens: g.total_tokens ?? 0,
      costUsd: g.cost_usd ?? 0,
      chats: (g.chats ?? []).map((r: any) => this.mapWebsiteRow(r)),
    };
  }

  private mapWebsiteDetail(d: any): WebsiteChatDetail {
    const t = d?.totals ?? {};
    return {
      conversationUuid: d?.conversation_uuid,
      title: d?.title ?? '',
      startedAt: d?.started_at,
      deletedByCustomer: !!d?.deleted_by_customer,
      customer: this.mapWebsiteCustomer(d?.customer),
      totals: {
        questions: t.questions ?? 0,
        images: t.images ?? 0,
        inputTokens: t.input_tokens ?? 0,
        outputTokens: t.output_tokens ?? 0,
        totalTokens: t.total_tokens ?? 0,
        costUsd: t.cost_usd ?? 0,
        thumbsUp: t.thumbs_up ?? 0,
        thumbsDown: t.thumbs_down ?? 0,
        avgReplySeconds: t.avg_reply_seconds ?? null,
        models: t.models ?? [],
      },
      messages: (d?.messages ?? []).map((m: any) => this.mapWebsiteMessage(m)),
    };
  }

  private mapWebsiteMessage(m: any): WebsiteChatMessage {
    return {
      id: m.id,
      role: m.role,
      content: m.content ?? '',
      createdAt: m.created_at ?? null,
      attachments: (m.attachments ?? []).map((a: ApiMessageAttachment) => this.mapAttachment(a)),
      components: m.role === 'assistant' ? normalizePebblComponents(m.components) : [],
      usage: m.usage ? { ...mapUsage(m.usage), partial: !!m.usage.partial } : null,
      elapsedSeconds: m.elapsed_seconds ?? m.usage?.elapsed_seconds ?? null,
      rating: m.rating ?? null,
    };
  }

  /** Normalise a raw feedback record (snake_case) into the view-model. */
  private mapFeedback(f: any): FeedbackItem {
    const reporter = f?.reporter;
    return {
      uuid: f?.uuid,
      category: f?.category ?? '',
      message: f?.message ?? '',
      status: f?.status ?? 'pending',
      createdBy: f?.created_by,
      agentType: f?.agent_type ?? null,
      // The API resolves the submitter for us (from the frontend or backend user
      // table, per agent_type), so the screen never has to load a user roster.
      reporter: reporter
        ? {
            id: reporter.id,
            name: reporter.name ?? '',
            email: reporter.email ?? '',
          }
        : null,
      createdAt: f?.created_at,
      updatedAt: f?.updated_at,
    };
  }
  /************************************
   * Feedback end
   ************************************/

  /************************************
   * Knowledge base start
   ************************************/
  listKnowledgeDocuments(opts: { limit?: number } = {}): Observable<KnowledgeDoc[]> {
    const params = new URLSearchParams();
    if (opts.limit != null) params.set('limit', String(opts.limit));
    return this.baseAiService.get(`${this.base}/knowledge-base/documents?${params}`).pipe(
      map((res: any) => (res.body?.payload?.items ?? []).map((d: any) => this.mapKnowledgeDoc(d))),
    );
  }

  /** Upload + parse a file (stored as `pending`); indexing happens separately. */
  uploadKnowledgeDocument(file: File): Observable<UploadedDoc> {
    const form = new FormData();
    form.append('file', file);
    return this.baseAiService
      .postFormData(`${this.base}/knowledge-base/documents`, form)
      .pipe(map((res: any) => this.mapUploadedDoc(res?.payload)));
  }

  getKnowledgeDocument(documentUuid: string): Observable<KnowledgeDocDetail> {
    return this.baseAiService
      .get(`${this.base}/knowledge-base/documents/${encodeURIComponent(documentUuid)}`)
      .pipe(map((res: any) => this.mapKnowledgeDetail(res.body?.payload)));
  }

  /**
   * Kick off background indexing (202); poll the document's status for progress.
   *
   * @param selectedColumns Columns from the first sheet — the flat form CSVs and
   *   single-sheet workbooks have always used. Ignored by the backend when `sheets`
   *   is given.
   * @param sheets Per-sheet column selection for a multi-sheet workbook. Omit to let
   *   the backend index every visible sheet.
   */
  indexKnowledgeDocument(
    documentUuid: string,
    selectedColumns: string[],
    sheets?: SheetSelectionInput[] | null,
  ): Observable<void> {
    return this.baseAiService
      .post(`${this.base}/knowledge-base/documents/${encodeURIComponent(documentUuid)}/index`, {
        selected_columns: selectedColumns,
        ...(sheets ? { sheets } : {}),
      })
      .pipe(map(() => undefined));
  }

  deleteKnowledgeDocument(documentUuid: string): Observable<void> {
    return this.baseAiService
      .delete(`${this.base}/knowledge-base/documents/${encodeURIComponent(documentUuid)}`)
      .pipe(map(() => undefined));
  }
  /************************************
   * Knowledge base end
   ************************************/

  /************************************
   * Chat attachments start
   ************************************/
  /**
   * Authorise a batch of images for one conversation, returning a presigned POST target
   * per image (in request order). The conversation need not exist yet — a new chat scopes
   * its attachments to the id it will be created with on the first turn.
   */
  requestAttachmentUploads(
    conversationUuid: string,
    items: AttachmentUploadIntent[],
  ): Observable<AttachmentUploadTicket[]> {
    return this.baseAiService
      .post(`${this.base}/attachments/uploads`, {
        conversation_uuid: conversationUuid,
        items: items.map((item) => ({
          file_name: item.fileName,
          content_type: item.contentType,
          size_bytes: item.sizeBytes,
          ...(item.width ? { width: item.width } : {}),
          ...(item.height ? { height: item.height } : {}),
        })),
      })
      .pipe(
        map((res: any) =>
          ((res?.payload?.items ?? []) as any[]).map((t) => ({
            attachmentUuid: t.attachment_uuid,
            postUrl: t.post_url,
            fields: t.fields ?? {},
            expiresIn: t.expires_in ?? 0,
          })),
        ),
      );
  }

  /**
   * Validate uploaded images and promote each to ready (or rejected). Validation is
   * per image — one rejected file never fails the batch.
   */
  confirmAttachmentUploads(attachmentUuids: string[]): Observable<ConfirmedAttachment[]> {
    return this.baseAiService
      .post(`${this.base}/attachments/confirm`, { attachment_uuids: attachmentUuids })
      .pipe(
        map((res: any) =>
          ((res?.payload?.items ?? []) as any[]).map((c) => ({
            attachmentUuid: c.attachment_uuid,
            status: c.status,
            url: c.url ?? null,
            fileName: c.file_name,
            contentType: c.content_type,
            sizeBytes: c.size_bytes ?? 0,
            width: c.width ?? null,
            height: c.height ?? null,
            error: c.error ?? null,
          })),
        ),
      );
  }

  /** Mint a fresh view URL for one image, for a tab left open past the URL's TTL. */
  resolveAttachmentUrl(attachmentUuid: string): Observable<string> {
    return this.baseAiService
      .get(`${this.base}/attachments/${encodeURIComponent(attachmentUuid)}/url`)
      .pipe(map((res: any) => res.body?.payload?.url ?? ''));
  }

  /** Remove an image the user pulled off the composer before sending. */
  deleteAttachment(attachmentUuid: string): Observable<void> {
    return this.baseAiService
      .delete(`${this.base}/attachments/${encodeURIComponent(attachmentUuid)}`)
      .pipe(map(() => undefined));
  }

  /**
   * Upload straight to S3. XHR rather than fetch because only XHR reports upload
   * progress, and no Authorization header is attached — this request leaves our origin,
   * so the bearer token must never ride along. The signed policy is S3's only credential.
   */
  uploadAttachmentToS3(
    ticket: AttachmentUploadTicket,
    file: File,
    onProgress?: (fraction: number) => void,
  ): Promise<void> {
    const form = new FormData();
    for (const [name, value] of Object.entries(ticket.fields)) {
      form.append(name, value);
    }
    // S3 requires the file field last.
    form.append('file', file);

    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('POST', ticket.postUrl);
      request.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
      };
      request.onload = () => {
        if (request.status >= 200 && request.status < 300) resolve();
        else reject(new Error(`Upload failed (${request.status}). Please try again.`));
      };
      request.onerror = () =>
        reject(new Error('Upload failed. Check your connection and try again.'));
      request.onabort = () => reject(new Error('Upload cancelled.'));
      request.send(form);
    });
  }

  /**
   * Read a local image's pixel dimensions so the UI can reserve an aspect-ratio box and
   * history renders without layout shift. Failures are non-fatal — the hints are optional.
   */
  readImageDimensions(file: File): Promise<{ width?: number; height?: number }> {
    const objectUrl = URL.createObjectURL(file);
    return new Promise<{ width?: number; height?: number }>((resolve) => {
      const image = new Image();
      image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
      image.onerror = () => resolve({});
      image.src = objectUrl;
    }).finally(() => URL.revokeObjectURL(objectUrl));
  }
  /************************************
   * Chat attachments end
   ************************************/

  /************************************
   * Chat stream start
   ************************************/
  /**
   * Stream a chat turn from `POST /pebbl/chat`, yielding parsed SSE events as they
   * arrive. Uses fetch (not HttpClient) so the response body can be read incrementally.
   *
   * @param opts.conversationUuid The thread to stream into.
   * @param opts.messageUuid Client-generated UUID for this turn's assistant message.
   * @param opts.message The user's prompt.
   * @param opts.model The selected chat model id (e.g. "anthropic/claude-haiku-5.5").
   * @param opts.thinkingLevel How much the model reasons before answering — one of the
   *   selected model's `thinking_levels` ids (e.g. "medium"). Applies to this turn only.
   * @param opts.attachmentUuids Confirmed image ids to send with this turn, in display
   *   order. They must have been authorised for this same conversation.
   * @param opts.regenerate True when re-running the question that produced the last
   *   answer: the backend replaces that answer instead of appending a new user turn.
   *   `messageUuid` must still be a fresh UUID (not the original message's) and
   *   `message` the original question verbatim.
   * @param opts.signal Abort signal wired to the Stop button.
   */
  async *streamChat(opts: {
    conversationUuid: string;
    messageUuid: string;
    message: string;
    model?: string;
    thinkingLevel?: string;
    attachmentUuids?: string[];
    regenerate?: boolean;
    signal?: AbortSignal;
  }): AsyncGenerator<PebblStreamEvent> {
    // Same service token the CredentialsInterceptor puts on the REST calls. It lives in memory
    // only (never localStorage), so it has to be read from ServiceTokenService — reading a
    // 'token' localStorage key sends an empty bearer, which the AI API rejects with a 401.
    const token = await firstValueFrom(this.serviceTokenService.ensureToken());
    if (!token) {
      throw new Error('Your session has expired. Please sign in again.');
    }
    const root = (environment.aiApiUrl || '').replace(/\/+$/, '');
    const res = await fetch(`${root}/${this.base}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
        // The AI API requires the client identity alongside the token — without it a valid token
        // still comes back 401. CredentialsInterceptor adds it to the HttpClient calls; a raw
        // fetch never passes through an interceptor, so it is set by hand here.
        [AUTH_CLIENT_HEADER]: AUTH_CLIENT,
      },
      body: JSON.stringify({
        message_uuid: opts.messageUuid,
        message: opts.message,
        conversation_uuid: opts.conversationUuid,
        ...(opts.model ? { model: opts.model } : {}),
        ...(opts.thinkingLevel ? { thinking_level: opts.thinkingLevel } : {}),
        ...(opts.attachmentUuids?.length
          ? { attachment_uuids: opts.attachmentUuids }
          : {}),
        ...(opts.regenerate ? { regenerate: true } : {}),
      }),
      signal: opts.signal,
    });
    if (!res.ok || !res.body) {
      throw new Error(await this.errorText(res));
    }
    yield* this.parseSse(res.body);
  }
  /************************************
   * Chat stream end
   ************************************/

  /************************************
   * Helpers start
   ************************************/
  private mapConversation(c: ApiConversation): Conversation {
    return {
      id: c.uuid,
      title: c.title,
      messageCount: c.message_count,
      updatedAt: c.updated_at,
    };
  }

  private mapMessage(m: ApiMessage): ChatMessage {
    if (m.role === 'user') {
      return {
        role: 'user',
        text: m.content,
        timeline: [],
        components: [],
        attachments: (m.attachments ?? []).map((a) => this.mapAttachment(a)),
        createdAt: m.created_at ?? undefined,
      };
    }
    return {
      role: 'assistant',
      text: m.content,
      timeline: [],
      components: normalizePebblComponents(m.components),
      uuid: m.uuid,
      feedback: m.feedback?.rating ?? null,
      usage: m.usage ? mapUsage(m.usage) : undefined,
      createdAt: m.created_at ?? undefined,
    };
  }

  private mapAttachment(a: ApiMessageAttachment): MessageAttachment {
    return {
      attachmentUuid: a.attachment_uuid,
      url: a.url,
      fileName: a.file_name,
      contentType: a.content_type,
      sizeBytes: a.size_bytes ?? 0,
      width: a.width ?? null,
      height: a.height ?? null,
    };
  }

  private mapKnowledgeDoc(d: any): KnowledgeDoc {
    return {
      id: d.document_uuid,
      fileName: d.file_name,
      status: d.status,
      totalChunks: d.total_chunks ?? 0,
      totalRows: d.total_rows ?? 0,
      indexedColumns: d.indexed_columns ?? [],
      indexedSheets: (d.indexed_sheets ?? []).map((s: any) => ({
        name: s.name ?? '',
        columns: s.columns ?? [],
        chunkCount: s.chunk_count ?? 0,
      })),
      createdAt: d.created_at,
      errorDetail: d.error_detail ?? undefined,
    };
  }

  /**
   * Normalise a parse preview. The backend returns one entry per worksheet plus a flat
   * copy of the first sheet; documents parsed before multi-sheet support have no
   * `sheets` array, so one is synthesised from the flat fields to keep the UI uniform.
   */
  private mapPreview(preview: any): DocumentPreview | undefined {
    if (!preview) return undefined;
    const rawSheets: any[] = Array.isArray(preview.sheets) ? preview.sheets : [];
    const columns: string[] = preview.columns ?? [];
    const rows: string[][] = preview.rows ?? [];
    const totalRows: number = preview.total_rows ?? 0;
    const sheets = rawSheets.length
      ? rawSheets.map((s) => ({
          name: s.name ?? '',
          index: s.index ?? 0,
          hidden: !!s.hidden,
          columns: s.columns ?? [],
          rows: s.rows ?? [],
          totalRows: s.total_rows ?? 0,
        }))
      : columns.length
        ? [{ name: '', index: 0, hidden: false, columns, rows, totalRows }]
        : [];
    if (!sheets.length) return undefined;
    return { sheets, columns, rows, totalRows };
  }

  /** Map a document detail, attaching the parse preview (table or markdown text). */
  private mapKnowledgeDetail(d: any): KnowledgeDocDetail {
    return {
      ...this.mapKnowledgeDoc(d),
      needsColumnSelection: !!d?.needs_column_selection,
      preview: this.mapPreview(d?.preview),
      text: d?.preview?.text ?? undefined,
    };
  }

  private mapUploadedDoc(p: any): UploadedDoc {
    return {
      documentUuid: p.document_uuid,
      fileName: p.file_name,
      needsColumnSelection: !!p.needs_column_selection,
      preview: this.mapPreview(p?.preview),
      text: p?.preview?.text ?? undefined,
    };
  }

  /** Best-effort human-readable message for a failed streaming request. */
  private async errorText(res: Response): Promise<string> {
    try {
      const body = await res.json();
      if (typeof body?.message === 'string') return body.message;
    } catch {
      /* Not JSON — fall through to the status line. */
    }
    return `Request failed (${res.status})`;
  }

  /**
   * Parse a Server-Sent Events body into typed chunks. Each event's `data:` lines carry
   * a JSON object; blank-line-separated events are yielded as they arrive.
   */
  private async *parseSse(body: ReadableStream<Uint8Array>): AsyncGenerator<PebblStreamEvent> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        let split: number;
        while ((split = buffer.indexOf('\n\n')) !== -1) {
          const block = buffer.slice(0, split);
          buffer = buffer.slice(split + 2);
          const event = this.parseSseBlock(block);
          if (event) yield event;
        }
      }
      const trailing = this.parseSseBlock(buffer);
      if (trailing) yield trailing;
    } finally {
      reader.releaseLock();
    }
  }

  /** Pull the JSON payload out of one SSE event block, ignoring `event:`/`id:` lines. */
  private parseSseBlock(block: string): PebblStreamEvent | undefined {
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
    }
    const payload = data.join('\n');
    if (!payload || payload === '[DONE]') return undefined;
    try {
      return JSON.parse(payload) as PebblStreamEvent;
    } catch {
      return undefined;
    }
  }
  /************************************
   * Helpers end
   ************************************/
}
